/**
 * @packageDocumentation
 * @category Component
 */

/* React deduplicates a change whose value it believes unchanged, and it learns
   that value by wrapping the element's own setter. Assigning `.value` directly
   can therefore leave the handler unfired, so the write goes through the
   prototype's setter, which is correct whether or not React is tracking this
   element. */
const writeValue = (select: HTMLSelectElement, value: string): void => {
  const descriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(select), 'value');
  if (descriptor?.set) {
    descriptor.set.call(select, value);
    return;
  }
  select.value = value;
};

const announce = (select: HTMLSelectElement): void => {
  const view = select.ownerDocument.defaultView;
  select.dispatchEvent(new (view === null ? Event : view.Event)('change', { bubbles: true }));
};

/** Puts the chosen value on the real element and tells React about it, so a
 *  consumer's `event.target` is a genuine select rather than an imitation. */
export const commitValue = (select: HTMLSelectElement, value: string): void => {
  writeValue(select, value);
  announce(select);
};

/** The multiple-selection counterpart, so `event.target.selectedOptions` reads
 *  the full selection. */
export const commitValues = (select: HTMLSelectElement, values: string[]): void => {
  const chosen = new Set(values);
  for (const option of Array.from(select.options)) option.selected = chosen.has(option.value);
  announce(select);
};
